"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { REFERRAL_KEYS, ROLE_KEYS } from "@/constants/listMyClub";
import type { ClubType } from "@/hooks/useListMyClubForm";
import { useTranslations } from "next-intl";

const CLUB_TYPE_OPTIONS: {
  value: ClubType;
  labelKey: string;
  descKey: string;
}[] = [
  {
    value: "non-spond",
    labelKey: "nonSpondClub",
    descKey: "nonSpondClubDescription",
  },
  { value: "spond", labelKey: "spondClub", descKey: "spondClubDescription" },
];

export function ClubTypeSelector({
  clubType,
  onClubTypeChange,
}: {
  clubType: ClubType;
  onClubTypeChange: (type: ClubType) => void;
}) {
  const t = useTranslations("listMyClub");

  return (
    <RadioGroup
      value={clubType}
      onValueChange={(v) => onClubTypeChange(v as ClubType)}
      aria-label={t("clubTypeLabel")}
      className="space-y-2"
    >
      {CLUB_TYPE_OPTIONS.map((option) => (
        <label
          key={option.value}
          htmlFor={`club-type-${option.value}`}
          className={`flex items-center gap-3 w-full text-left rounded-lg border p-3 cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 ${
            clubType === option.value
              ? "border-foreground bg-muted"
              : "border-border"
          }`}
        >
          <RadioGroupItem
            id={`club-type-${option.value}`}
            value={option.value}
            className="focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <div>
            <div className="font-medium">{t(option.labelKey)}</div>
            <div className="text-sm text-muted-foreground">
              {t(option.descKey)}
            </div>
          </div>
        </label>
      ))}
    </RadioGroup>
  );
}

export function ListMyClubFormFields({
  formData,
  isSubmitting,
  hasError,
  contactError,
  roleError,
  errorMessage,
  idPrefix,
  onChange,
  onRoleChange,
  onContactPreferenceChange,
  onReferralChange,
}: {
  formData: {
    clubType: ClubType;
    clubName: string;
    contactName: string;
    email: string;
    phone: string;
    contactPreference: "email" | "phone";
    role: string;
    referralSource: string;
  };
  isSubmitting: boolean;
  hasError: boolean;
  contactError: string;
  roleError: string;
  errorMessage: string;
  idPrefix: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRoleChange: (value: string) => void;
  onContactPreferenceChange: (value: "email" | "phone") => void;
  onReferralChange: (value: string) => void;
}) {
  const t = useTranslations("listMyClub");
  const isNonSpond = formData.clubType === "non-spond";

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-club-name`}>{t("clubName")}</Label>
        <Input
          id={`${idPrefix}-club-name`}
          name="clubName"
          placeholder={t("clubNamePlaceholder")}
          value={formData.clubName}
          onChange={onChange}
          maxLength={100}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-contact-name`}>{t("contactName")}</Label>
        <Input
          id={`${idPrefix}-contact-name`}
          name="contactName"
          placeholder={t("contactNamePlaceholder")}
          value={formData.contactName}
          onChange={onChange}
          maxLength={100}
          required
          disabled={isSubmitting}
        />
      </div>
      <p
        id={`${idPrefix}-contact-hint`}
        className="text-sm text-muted-foreground"
      >
        {t("contactHint")}
      </p>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-email`}>{t("email")}</Label>
        <Input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          placeholder={t("emailPlaceholder")}
          value={formData.email}
          onChange={onChange}
          maxLength={255}
          disabled={isSubmitting}
          aria-describedby={`${idPrefix}-contact-hint`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-phone`}>{t("phone")}</Label>
        <Input
          id={`${idPrefix}-phone`}
          name="phone"
          type="tel"
          placeholder={t("phonePlaceholder")}
          value={formData.phone}
          onChange={onChange}
          maxLength={50}
          disabled={isSubmitting}
          aria-describedby={`${idPrefix}-contact-hint`}
        />
      </div>

      <div className="space-y-2">
        <Label>{t("contactPreference")}</Label>
        <RadioGroup
          value={formData.contactPreference}
          onValueChange={(v) =>
            onContactPreferenceChange(v as "email" | "phone")
          }
          className="flex gap-4"
        >
          <label
            htmlFor={`${idPrefix}-pref-email`}
            className="flex items-center gap-2 cursor-pointer"
          >
            <RadioGroupItem
              id={`${idPrefix}-pref-email`}
              value="email"
              disabled={isSubmitting}
            />
            <span className="text-sm">{t("preferEmail")}</span>
          </label>
          <label
            htmlFor={`${idPrefix}-pref-phone`}
            className="flex items-center gap-2 cursor-pointer"
          >
            <RadioGroupItem
              id={`${idPrefix}-pref-phone`}
              value="phone"
              disabled={isSubmitting}
            />
            <span className="text-sm">{t("preferPhone")}</span>
          </label>
        </RadioGroup>
      </div>

      {contactError && (
        <p className="text-sm text-destructive">{contactError}</p>
      )}

      {isNonSpond && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-role`}>{t("role")}</Label>
          <Select
            value={formData.role}
            onValueChange={onRoleChange}
            disabled={isSubmitting}
          >
            <SelectTrigger
              id={`${idPrefix}-role`}
              aria-required={true}
              aria-invalid={Boolean(roleError)}
              aria-describedby={
                roleError ? `${idPrefix}-role-error` : undefined
              }
            >
              <SelectValue placeholder={t("rolePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {ROLE_KEYS.map((key) => (
                <SelectItem key={key} value={key}>
                  {t(key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {roleError && (
            <p
              id={`${idPrefix}-role-error`}
              className="text-sm text-destructive"
            >
              {roleError}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-referral`}>{t("referralLabel")}</Label>
        <Select
          value={formData.referralSource}
          onValueChange={onReferralChange}
          disabled={isSubmitting}
        >
          <SelectTrigger id={`${idPrefix}-referral`}>
            <SelectValue placeholder={t("referralPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {REFERRAL_KEYS.map((key) => (
              <SelectItem key={key} value={key}>
                {t(key)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(errorMessage || hasError) && (
        <p className="text-sm text-destructive">{errorMessage || t("error")}</p>
      )}
    </>
  );
}
